// src/components/permissions/PermissionDeniedOverlay.js
import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

function LocationHowTo() {
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        How to turn location back on
      </Typography>
      <Typography variant="body2">
        <strong>iOS Safari</strong>: Settings → Safari → Location → Allow. Then
        reload Inssa.
      </Typography>
      <Typography variant="body2">
        <strong>Android Chrome</strong>: Chrome menu → Settings → Site settings →
        Location → Allow for inssa.us. Then reload Inssa.
      </Typography>
    </Stack>
  );
}

export default function PermissionDeniedOverlay({
  show,
  onClose,
  mode = "recenter",
  onApproxRecenter, // optional, used for recenter mode
  approxMeta, // optional { city, region, country, accuracyRadiusKm }
  approxReady = false,
  approxLoading = false,
}) {
  if (!show) return null;

  const title =
    mode === "navigation" ? "Walking directions need GPS" : "We can only guess your spot";

  const details =
    mode === "navigation"
      ? "You can still search and explore capsules, but turn by turn walking directions need your device location."
      : "You told your browser “no thanks” on location. Totally fine. We can still place you on the map, but only approximately, based on your connection.";

  const approxLine =
    mode !== "navigation" && approxMeta
      ? `Approx area: ${[approxMeta.city, approxMeta.region, approxMeta.country]
          .filter(Boolean)
          .join(", ")}${
          approxMeta.accuracyRadiusKm ? ` (about ${approxMeta.accuracyRadiusKm} km)` : ""
        }.`
      : null;

  const buttonSx = {
    p: "12px 20px",
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000000,
        bgcolor: (theme) => alpha(theme.palette.common.black, 0.92),
        color: "common.white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        textAlign: "left",
      }}
    >
      <Box sx={{ maxWidth: 560, width: "100%" }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {details}
        </Typography>

        {approxLine && (
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            {approxLine}
          </Typography>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          If you want the exact dot, turn location back on and reload. If you want a good
          enough guess right now, use the approximate recenter button below.
        </Typography>

        <LocationHowTo />

        {/* Buttons: stacked vertically for mobile clarity */}
        <Stack spacing={1.25} sx={{ mt: 3, width: "100%" }}>
          {mode !== "navigation" && typeof onApproxRecenter === "function" && (
            <Button
              fullWidth
              variant="contained"
              onClick={onApproxRecenter}
              disabled={approxLoading}
              sx={buttonSx}
            >
              {approxLoading
                ? "Finding your approximate spot…"
                : approxReady
                ? "Recenter approximately"
                : "Try approximate recenter"}
            </Button>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => window.location.reload()}
            sx={buttonSx}
          >
            Reload after enabling
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              ...buttonSx,
              borderColor: (theme) => alpha(theme.colors.white, 0.4),
              color: "common.white",
              "&:hover": { borderColor: (theme) => alpha(theme.colors.white, 0.7) },
            }}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
